// Playbook Phase A.1 (034 follow-up) — starter Formation/Front templates
// and the "+ Add Player" quick-position lists. These are CONVENIENCES
// only, never football-legality rules: every marker they produce is
// fully editable (position, label, removable) immediately after being
// placed, exactly like a manually-added marker. Limited to 11v11 for
// this pass — non-11v11/custom formats keep using Blank/Custom, per
// product decision (do not fabricate universal 7v7/8v8/etc. alignments
// yet). Coordinates use the same football-relative system as everything
// else in Playbook (x centered -1..1, y yards relative to LOS, negative
// = the active/authoring unit's own backfield) — no pixels, no
// unit_type baked into the coordinate values themselves.

// Coordinates below are a COACHING DIAGRAM, not a literal to-scale GPS
// trace — but after an earlier pass over-corrected toward maximizing
// empty space, the standing priority is now (1) football-plausible
// relationships, (2) a readable diagram, (3) avoiding marker overlap —
// in that order. Spacing/depth is calibrated to look like real football
// alignment first; most neighboring pairs still clear the marker's own
// on-screen diameter with a visible gap as a side effect of that, but
// where a realistic depth (see QB_UNDER_CENTER below) would otherwise
// force an unrealistic exaggeration just to keep circles apart, the
// realistic value wins and a little visual overlap is accepted instead.
//
// y = 0 is the actual Line of Scrimmage (unchanged, never moved). But a
// marker is a drawn CIRCLE, not a point — checking only its center
// against y = 0 is not enough to know whether it LOOKS onside, since
// the marker's own front edge can extend past its center by
// VISUAL_RADIUS. CENTER_DEPTH/OL_DEPTH below are therefore derived —
// not eyeballed — from fieldCoordinates.js's centerBoundaryY()/
// onLineBoundaryY(), evaluated for the standard offense/defense
// field_config ({before_los_yd:15, after_los_yd:15}, the only config
// these starter templates ever use):
//
//   VISUAL_RADIUS (PlaybookMarker.js)   = 9 viewBox units
//   viewBox units per yard (this config) = VIEW_HEIGHT / 30 = 8
//   VISUAL_RADIUS in yards                = 9 / 8 = 1.125yd
//   LOS_BAND_HALF_YD (fieldCoordinates.js) = 0.15yd
//
//   onLineBoundaryY  = -LOS_BAND_HALF_YD - radiusYd = -0.15 - 1.125 = -1.275
//     (front edge touches the band's NEAR/current-unit edge, never past it)
//   centerBoundaryY  =  LOS_BAND_HALF_YD - radiusYd =  0.15 - 1.125 = -0.975
//     (Center's one exception: front edge may reach the band's FAR/
//     opponent edge, never past it — he's the player who snaps the ball)
//
// Every offensive player here sits AT or behind (never past/shallower
// than) its role's boundary — never treated as a real "on the line"
// rule (there is no on_line/off_line concept anywhere in this schema;
// that stays a future semantic/tolerance-based concept, not a y-value
// comparison). Guards/Tackles share the same normal boundary as every
// other non-Center marker — a subtle stagger versus Center, not a
// curved line. On-line pass-catchers (an attached TE, a boundary/
// split-end WR) sit at that same normal boundary; receivers who'd be
// off the line in real alignment (a flanker WR, a slot WR) are visibly
// backed off further, so a coach can read on-line vs. off-line
// receivers at a glance without any stored metadata.
const OL_DEPTH = -1.275;
const CENTER_DEPTH = -0.975;
const OFF_LINE_DEPTH = OL_DEPTH - 0.5; // visibly deeper than the on-line row

const OFFENSE_LINE = [
  { role_label: 'LT', x: -0.36, y: OL_DEPTH },
  { role_label: 'LG', x: -0.18, y: OL_DEPTH },
  { role_label: 'C',  x: 0.00,  y: CENTER_DEPTH },
  { role_label: 'RG', x: 0.18,  y: OL_DEPTH },
  { role_label: 'RT', x: 0.36,  y: OL_DEPTH },
];
// TE and the boundary/split-end WR (weak side) are on the line, level
// with the Guards/Tackles, attached with a modest gap rather than
// floating off on their own. The flanker WR (strong side) and the slot
// WR (weak side, 3-WR sets) are off the line, backed off just enough to
// read as a different depth — not multiple yards deeper.
const TE_SLOT = { role_label: 'TE', x: 0.52, y: OL_DEPTH };
const WR_STRONG = { role_label: 'WR', x: 0.68, y: OFF_LINE_DEPTH };
const WR_WEAK = { role_label: 'WR', x: -0.68, y: OL_DEPTH };
const WR_SLOT_WEAK = { role_label: 'WR', x: -0.50, y: OFF_LINE_DEPTH };

// Under-center QB depth: the Formation Editor's own collision guard
// requires every starter template to initialize collision-free (no
// exceptions — a previous pass accepted a deliberate, closer C/QB
// overlap here for extra realism; that is no longer allowed at
// creation time). This is the smallest depth beyond CENTER_DEPTH that
// still clears the 18-viewBox-unit (2 * VISUAL_RADIUS) minimum
// separation from the Center, now that Center itself sits deeper.
const QB_UNDER_CENTER = -3.4;

export const OFFENSE_STARTER_FORMATIONS = [
  {
    key: 'i_formation',
    label: 'I Formation',
    markers: [
      ...OFFENSE_LINE, TE_SLOT, WR_WEAK, WR_STRONG,
      { role_label: 'QB', x: 0, y: QB_UNDER_CENTER },
      { role_label: 'FB', x: 0, y: -6.0 },
      { role_label: 'HB', x: 0, y: -10.0 },
    ],
  },
  {
    key: 'pro_set',
    label: 'Pro Set',
    markers: [
      ...OFFENSE_LINE, TE_SLOT, WR_WEAK, WR_STRONG,
      { role_label: 'QB', x: 0, y: QB_UNDER_CENTER },
      { role_label: 'FB', x: -0.15, y: -5.2 },
      { role_label: 'HB', x: 0.15, y: -5.2 },
    ],
  },
  {
    key: 'singleback',
    label: 'Singleback',
    markers: [
      ...OFFENSE_LINE, TE_SLOT, WR_WEAK, WR_STRONG, WR_SLOT_WEAK,
      { role_label: 'QB', x: 0, y: QB_UNDER_CENTER },
      { role_label: 'HB', x: 0, y: -6.2 },
    ],
  },
  {
    key: 'shotgun',
    label: 'Shotgun',
    markers: [
      ...OFFENSE_LINE, TE_SLOT, WR_WEAK, WR_STRONG, WR_SLOT_WEAK,
      { role_label: 'QB', x: 0, y: -5 },
      { role_label: 'HB', x: -0.16, y: -4.7 },
    ],
  },
  {
    key: 'pistol',
    label: 'Pistol',
    markers: [
      ...OFFENSE_LINE, TE_SLOT, WR_WEAK, WR_STRONG, WR_SLOT_WEAK,
      { role_label: 'QB', x: 0, y: -4 },
      { role_label: 'HB', x: 0, y: -7 },
    ],
  },
];

// Defense uses the SAME normal on-line boundary as offense (there is no
// Center-style forward exception for any defensive role) — a coincidence
// of the derivation, not a hardcoded assumption: both are simply "the
// deepest a non-Center marker's front edge can sit while still touching
// (never crossing) the LOS band's near edge" for this same field_config,
// so the number happens to match OL_DEPTH above. Kept as its own named
// constant (rather than importing OL_DEPTH) so this section reads
// correctly on its own. The outer CBs (a press-style alignment) also
// sat close enough to the LOS (-1) to cross the band before this pass;
// pulled back to the same boundary for the same reason as the DL.
const DL_DEPTH = -1.275;
const CB_PRESS_DEPTH = -1.275;

export const DEFENSE_STARTER_FORMATIONS = [
  {
    key: '4_3',
    label: '4-3',
    markers: [
      { role_label: 'DE', x: -0.35, y: DL_DEPTH },
      { role_label: 'DT', x: -0.1, y: DL_DEPTH },
      { role_label: 'DT', x: 0.1, y: DL_DEPTH },
      { role_label: 'DE', x: 0.35, y: DL_DEPTH },
      { role_label: 'OLB', x: -0.25, y: -3.5 },
      { role_label: 'MLB', x: 0, y: -4 },
      { role_label: 'OLB', x: 0.25, y: -3.5 },
      { role_label: 'CB', x: -0.85, y: CB_PRESS_DEPTH },
      { role_label: 'CB', x: 0.85, y: CB_PRESS_DEPTH },
      { role_label: 'SS', x: 0.3, y: -6.5 },
      { role_label: 'FS', x: 0, y: -9 },
    ],
  },
  {
    key: '3_4',
    label: '3-4',
    markers: [
      { role_label: 'DE', x: -0.25, y: DL_DEPTH },
      { role_label: 'NT', x: 0, y: DL_DEPTH },
      { role_label: 'DE', x: 0.25, y: DL_DEPTH },
      { role_label: 'OLB', x: -0.5, y: -3 },
      { role_label: 'ILB', x: -0.15, y: -3.5 },
      { role_label: 'ILB', x: 0.15, y: -3.5 },
      { role_label: 'OLB', x: 0.5, y: -3 },
      { role_label: 'CB', x: -0.85, y: CB_PRESS_DEPTH },
      { role_label: 'CB', x: 0.85, y: CB_PRESS_DEPTH },
      { role_label: 'SS', x: 0.25, y: -6.5 },
      { role_label: 'FS', x: 0, y: -9 },
    ],
  },
  {
    key: '4_2_5',
    label: '4-2-5',
    markers: [
      { role_label: 'DE', x: -0.35, y: DL_DEPTH },
      { role_label: 'DT', x: -0.1, y: DL_DEPTH },
      { role_label: 'DT', x: 0.1, y: DL_DEPTH },
      { role_label: 'DE', x: 0.35, y: DL_DEPTH },
      { role_label: 'LB', x: -0.15, y: -3.5 },
      { role_label: 'LB', x: 0.15, y: -3.5 },
      { role_label: 'CB', x: -0.85, y: CB_PRESS_DEPTH },
      { role_label: 'CB', x: 0.85, y: CB_PRESS_DEPTH },
      { role_label: 'CB', x: -0.55, y: -2 },
      { role_label: 'SS', x: 0.25, y: -6.5 },
      { role_label: 'FS', x: 0, y: -9 },
    ],
  },
  {
    key: 'nickel',
    label: 'Nickel',
    markers: [
      { role_label: 'DE', x: -0.25, y: DL_DEPTH },
      { role_label: 'NT', x: 0, y: DL_DEPTH },
      { role_label: 'DE', x: 0.25, y: DL_DEPTH },
      { role_label: 'LB', x: -0.3, y: -3.5 },
      { role_label: 'LB', x: 0, y: -4 },
      { role_label: 'LB', x: 0.3, y: -3.5 },
      { role_label: 'CB', x: -0.85, y: CB_PRESS_DEPTH },
      { role_label: 'CB', x: 0.85, y: CB_PRESS_DEPTH },
      { role_label: 'CB', x: -0.55, y: -2 },
      { role_label: 'SS', x: 0.25, y: -6.5 },
      { role_label: 'FS', x: 0, y: -9 },
    ],
  },
];

export function startersForUnitType(unitType) {
  if (unitType === 'offense') return OFFENSE_STARTER_FORMATIONS;
  if (unitType === 'defense') return DEFENSE_STARTER_FORMATIONS;
  return []; // Special Teams — intentionally no starters yet, unchanged this pass
}

// "+ Add Player" quick-position lists — suggestions only, never
// authoritative. "Custom" (handled by the caller, not listed here)
// always remains available so a coach can enter any terminology
// (H, Y, Z, Rover, Monster, etc.).
export const QUICK_POSITIONS = {
  offense: ['QB', 'HB', 'FB', 'WR', 'TE', 'C', 'G', 'T'],
  defense: ['DE', 'DT', 'NT', 'ILB', 'OLB', 'LB', 'CB', 'S'],
  special_teams: [],
};

/** A simple, deterministic staggered spawn position for a manually-added
 * marker — never exactly on top of an existing one, no layout engine.
 * Six per row, each row 2.5yd deeper into the active unit's own
 * backfield (y stays well clear of the LOS boundary the editor
 * otherwise enforces on drag). 2.5yd was previously 1.2yd, which left
 * same-column markers in wrapped rows only 9.6 viewBox units apart —
 * under the Formation Editor's own 18-unit collision minimum (2 *
 * VISUAL_RADIUS). This is just the starting grid; the caller
 * additionally runs every candidate through the same collision-guard
 * projection used for dragging, which is what actually GUARANTEES no
 * overlap (including against a loaded starter template's own markers,
 * which don't follow this grid at all) — this wider step just means
 * that guard rarely needs to nudge a freshly-spawned player at all. */
export function nextSpawnPosition(existingMarkerCount) {
  const col = existingMarkerCount % 6;
  const row = Math.floor(existingMarkerCount / 6);
  const x = -0.75 + col * 0.3;
  const y = -2.5 - row * 2.5;
  return { x: Math.max(-1, Math.min(1, x)), y };
}
